import classNames from 'classnames';
import {
  ElementType,
  FC,
  MouseEvent,
  MouseEventHandler,
  MutableRefObject,
  useCallback,
  useRef,
} from 'react';

import { IcArrowUp16Icon } from '@gem/icons';
import { IconButton, TIconButtonTheme, IconView, IconViewSizes } from '@gem/ui-kit';

import styles from './CallActionsPanelButton.styles.m.css';

export interface ICallActionsPanelButtonProps {
  theme: TIconButtonTheme;
  className?: string;
  Icon: ElementType;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onArrowClick?: MouseEventHandler<HTMLButtonElement>;
  setRef?: MutableRefObject<HTMLButtonElement | null>;
  setArrowRef?: MutableRefObject<HTMLButtonElement | null>;
  isArrowButtonPressed?: boolean;
  isActive?: boolean;
}

export const CallActionsPanelButton: FC<ICallActionsPanelButtonProps> = ({
  theme,
  Icon,
  className,
  isLoading,
  isDisabled,
  onClick,
  onArrowClick,
  isArrowButtonPressed,
  setRef,
  isActive,
}) => {
  const spanContainer = useRef<HTMLSpanElement>(null);
  const iconClasses = classNames(styles.icon, {
    [styles.disabledIcon]: isDisabled || isLoading,
    [styles.hiddenIcon]: isLoading,
  });

  const onButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (onClick && spanContainer.current && !spanContainer.current.contains(event.target)) {
        onClick(event);
      }
    },
    [onClick]
  );

  const onArrowButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (onArrowClick && !isDisabled) {
        onArrowClick(event);
      }
    },
    [onArrowClick, isDisabled]
  );

  return (
    <IconButton
      setRef={setRef}
      className={classNames(styles.button, className)}
      Icon={Icon}
      size={IconViewSizes.Large}
      iconSize={IconViewSizes.Medium}
      iconClassName={styles.mainIcon}
      theme={theme}
      onClick={onButtonClick}
      isDisabled={isDisabled}
    >
      <span
        ref={spanContainer}
        className={styles.arrowButtonContainer}
      >
        <span
          onClick={onArrowButtonClick}
          className={classNames(styles.arrowButton, {
            [styles.pressed]: isArrowButtonPressed,
            [styles.pressedActive]: isArrowButtonPressed && isActive,
            [styles.isActive]: isActive,
          })}
        >
          <IconView
            Icon={IcArrowUp16Icon}
            size={IconViewSizes.Small}
            className={classNames(styles.icon, iconClasses)}
          />
        </span>
      </span>
    </IconButton>
  );
};
